import {
  ArrowLeft,
  Lightbulb,
  Palette,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";

interface Challenge {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const challenges: Challenge[] = [
  {
    id: "foot-traffic",
    title: "Low Foot Traffic",
    description: "Struggling to attract visitors and increase dwell time",
    icon: <Users className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
  {
    id: "brand-differentiation",
    title: "Brand Differentiation",
    description: "Standing out in a competitive market landscape",
    icon: <Target className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
  {
    id: "customer-engagement",
    title: "Customer Engagement",
    description: "Creating memorable experiences that drive loyalty",
    icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
  {
    id: "revenue-optimization",
    title: "Revenue Optimization",
    description: "Maximizing profitability from existing space",
    icon: <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
  {
    id: "space-utilization",
    title: "Space Utilization",
    description: "Making the most of underused areas and zones",
    icon: <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
  {
    id: "cultural-identity",
    title: "Cultural Identity",
    description: "Developing a unique cultural personality and vibe",
    icon: <Palette className="w-5 h-5 sm:w-6 sm:h-6" />,
  },
];

interface Props {
  businessType: string;
  onChallengesSelect: (challenges: string[]) => void;
  onBack: () => void;
}

export default function ChallengeSelection({
  businessType,
  onChallengesSelect,
  onBack,
}: Props) {
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);

  const handleChallengeToggle = (challengeId: string) => {
    setSelectedChallenges((prev) =>
      prev.includes(challengeId)
        ? prev.filter((id) => id !== challengeId)
        : [...prev, challengeId]
    );
  };

  const handleContinue = () => {
    if (selectedChallenges.length > 0) {
      onChallengesSelect(selectedChallenges);
    }
  };

  // Get business type display name
  const getBusinessDisplayName = (type: string) => {
    const businessMap: { [key: string]: string } = {
      property: "Property Management",
      hospitality: "Hospitality",
      retail: "Retail",
    };
    return businessMap[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/3 right-1/3 w-32 h-32 sm:w-40 sm:h-40 bg-purple-500 rounded-full filter blur-3xl animate-pulse delay-300"></div>
        <div className="absolute bottom-1/3 left-1/3 w-24 h-24 sm:w-32 sm:h-32 bg-pink-500 rounded-full filter blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/6 w-20 h-20 sm:w-28 sm:h-28 bg-blue-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Compact container with proper footer spacing */}
      <div className="relative z-10 min-h-screen px-6 py-12 sm:px-8 sm:py-16 md:px-12 md:py-20 lg:px-16 lg:py-24 pb-32 sm:pb-36 md:pb-40">
        <div className="max-w-3xl mx-auto space-y-8 sm:space-y-12">
          {/* Compact Header */}
          <div className="text-center">
            {/* Breadcrumb */}
            <div className="inline-flex items-center bg-purple-500/20 backdrop-blur-sm rounded-full px-3 py-2 sm:px-4 mb-6 border border-purple-500/30">
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse mr-2 sm:mr-3"></div>
              <span className="text-purple-400 text-xs sm:text-sm font-medium">
                {getBusinessDisplayName(businessType).toUpperCase()} • STEP 2 OF
                4
              </span>
            </div>

            {/* Smaller heading */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent leading-tight">
              What challenges are you facing?
            </h2>
            <p className="text-base sm:text-lg text-slate-300 max-w-xl mx-auto leading-relaxed mb-6">
              Select all that apply. Our cultural intelligence will be tailored
              to address your specific needs.
            </p>

            {/* Challenge counter */}
            <div className="inline-flex items-center bg-slate-800/60 backdrop-blur-sm rounded-full px-3 py-2 border border-slate-600/50">
              <span className="text-slate-300 text-xs sm:text-sm">
                {selectedChallenges.length} challenge
                {selectedChallenges.length !== 1 ? "s" : ""} selected
              </span>
            </div>
          </div>

          {/* Constrained Challenge Grid */}
          <div className="max-w-2xl mx-auto">
            <div className="grid gap-3 sm:gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {challenges.map((challenge, index) => {
                const isSelected = selectedChallenges.includes(challenge.id);

                return (
                  <button
                    key={challenge.id}
                    onClick={() => handleChallengeToggle(challenge.id)}
                    className={`group relative p-3 sm:p-4 md:p-5 rounded-xl border-2 transition-all duration-500 text-left hover:shadow-xl min-h-[110px] sm:min-h-[120px] ${
                      isSelected
                        ? "border-purple-500 bg-slate-800/60 backdrop-blur-sm shadow-xl shadow-purple-500/20 scale-105"
                        : "border-slate-600/50 bg-slate-800/40 backdrop-blur-sm hover:border-purple-400/60 hover:bg-slate-800/60 hover:scale-102"
                    }`}
                    style={{
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <div className="relative z-10 h-full flex flex-col">
                      {/* Compact icon */}
                      <div
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center mb-2 sm:mb-3 transition-all duration-300 flex-shrink-0 ${
                          isSelected
                            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                            : "bg-gradient-to-r from-slate-700 to-slate-600 text-slate-300 group-hover:from-purple-500/80 group-hover:to-pink-500/80 group-hover:text-white"
                        }`}
                      >
                        {challenge.icon}
                      </div>

                      {/* Compact content */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-white group-hover:text-purple-100 transition-colors leading-tight">
                            {challenge.title}
                          </h3>
                          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed group-hover:text-slate-300 transition-colors">
                            {challenge.description}
                          </p>
                        </div>
                      </div>

                      {/* Small selection indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                      )}
                    </div>

                    {/* Animated border effect */}
                    <div
                      className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                        isSelected ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 opacity-20 animate-pulse"></div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-center sm:gap-6">
            <button
              onClick={onBack}
              className="flex items-center justify-center px-4 py-3 sm:px-6 text-slate-300 hover:text-white transition-colors group order-2 sm:order-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm sm:text-base">Change Business Type</span>
            </button>

            <button
              onClick={handleContinue}
              disabled={selectedChallenges.length === 0}
              className={`w-full sm:w-auto px-6 py-4 sm:px-8 rounded-xl font-semibold text-white transition-all duration-300 order-1 sm:order-2 text-sm sm:text-base ${
                selectedChallenges.length > 0
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:shadow-lg hover:shadow-purple-500/30 hover:scale-105"
                  : "bg-slate-600/50 cursor-not-allowed opacity-50"
              }`}
            >
              Continue with Property Details ({selectedChallenges.length})
            </button>
          </div>

          {/* Selected challenges preview */}
          {selectedChallenges.length > 0 && (
            <div className="text-center">
              <div className="inline-flex flex-wrap gap-2 justify-center max-w-full">
                {selectedChallenges.map((challengeId) => {
                  const challenge = challenges.find(
                    (c) => c.id === challengeId
                  );
                  return (
                    <span
                      key={challengeId}
                      className="px-3 py-1 bg-purple-500/20 text-purple-300 text-xs sm:text-sm rounded-full border border-purple-500/30"
                    >
                      {challenge?.title}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
